
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

interface WorkExperience {
  id: string;
  company: string;
  position: string;
  start_date: string;
  end_date: string | null;
  is_current: boolean;
  description: string | null;
}

interface WorkExperienceListProps {
  userId: string;
  onEdit: (experience: WorkExperience) => void;
  onRefresh: () => void;
}

const WorkExperienceList: React.FC<WorkExperienceListProps> = ({ userId, onEdit, onRefresh }) => {
  const [experiences, setExperiences] = useState<WorkExperience[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchWorkExperiences();
  }, [userId]);

  const fetchWorkExperiences = async () => {
    try {
      setIsLoading(true);
      if (!userId) return;

      const { data, error } = await supabase
        .from('work_experience')
        .select('*')
        .eq('job_seeker_id', userId)
        .order('is_current', { ascending: false })
        .order('end_date', { ascending: false })
        .order('start_date', { ascending: false });

      if (error) throw error;
      setExperiences(data || []);
    } catch (error: any) {
      console.error('Error fetching work experiences:', error.message);
      toast({
        title: "Error",
        description: "Failed to load work experiences",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('work_experience')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setExperiences(experiences.filter(exp => exp.id !== id));
      onRefresh();
      
      toast({
        title: "Experience deleted",
        description: "Work experience has been successfully deleted.",
      });
    } catch (error: any) {
      console.error('Error deleting work experience:', error.message);
      toast({
        title: "Error",
        description: "Failed to delete work experience",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    return format(new Date(dateString), 'MMM yyyy');
  };

  if (isLoading) {
    return <div className="py-4 text-center text-gray-600">Loading work experiences...</div>;
  }

  if (experiences.length === 0) {
    return <div className="py-4 text-center text-gray-600">No work experiences added yet</div>;
  }

  return (
    <div className="space-y-4">
      {experiences.map((experience) => (
        <div 
          key={experience.id} 
          className="p-4 bg-white border border-[#0EA5E9]/30 rounded-md hover:shadow-sm transition-shadow"
        >
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <h4 className="font-medium text-gray-800">{experience.position}</h4>
              <p className="text-gray-600">{experience.company}</p>
              <p className="text-sm text-gray-500">
                {formatDate(experience.start_date)} - {experience.is_current ? 'Present' : formatDate(experience.end_date)}
              </p>
              {experience.description && (
                <p className="text-gray-600 text-sm mt-2">{experience.description}</p>
              )}
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => onEdit(experience)}
                className="text-gray-500 hover:text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => handleDelete(experience.id)}
                className="text-gray-500 hover:text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WorkExperienceList;
